import { CheckCircle2, ChevronDown, ChevronRight, CircleDot, FileText, Headphones, PackageCheck, RotateCcw, Search, ShieldCheck, Star, Truck, XCircle, } from "lucide-react";
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchMyOrders,
  cancelOrder,
  requestReturnOrder,
  requestReplacementOrder,
  fetchOrderActions,
} from "../../../Redux/custmoer_orders/slice";
import { getKeepShopping } from "../../../Redux/ActivityApi/slice";
import { fetchBuyAgainProducts } from "../../../Redux/buyAgain/slice";
import { BuyAgainCard } from "../../../Redux/buyAgain/types";
import { OrderItem, OrderStatus, SalarOrder, } from "./OrdersTypes";
import { transformOrdersToSalarOrders } from "./dataTransformers";
import "./OrdersPage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import { useCart } from "../../../context/CartContext";
import Pagenation from "../../globalcomponents/pagenation/Pagenation";
import ChatPanel from "../../globalcomponents/ChatPanel/ChatPanel";
import axios from "axios";
import { getCookie } from "../../../utility/getCookie";
import { startCashfreeCheckout } from "../../../utility/cashfreeCheckout";

type OrdersTab = "orders" | "not-shipped";
type ActionType = "CANCEL" | "RETURN" | "REPLACE";

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(value);

const PLACEHOLDER_IMAGE = "/assets/images/logos/salarlogo.png";

const getStatusIcon = (status: OrderStatus) => {
    if (status === "shipped" || status === "out-for-delivery") {
        return (<Truck size={21} />);
    }
    if (status === "delivered") {
        return (<CheckCircle2 size={21} />);
    }
    if (status === "returned") {
        return (<RotateCcw size={21} />);
    }
    if (status === "cancelled") {
        return (<XCircle size={21} />);
    }
    return (<PackageCheck size={21} />);
};

const paymentStatusClass = (status: string): string => {
    if (status === "Success" || status === "COMPLETED") return "payment-success";
    if (status === "Failed" || status === "FAILED") return "payment-failed";
    if (status === "Refunded" || status === "REFUNDED") return "payment-refunded";
    return "payment-pending";
};

const refundStatusClass = (status: string): string => {
    if (status === "REFUNDED" || status === "COMPLETED") return "refund-completed";
    return "refund-pending";
};

const isSubscriptionOrder = (order: SalarOrder): boolean => {
    const name = order.items?.[0]?.title?.toLowerCase() || "";
    return name.includes("membership") || name.includes("subscription");
};

const getOrderDetailsRoute = (order: SalarOrder): string => {
    return isSubscriptionOrder(order) ? "/SubsOrderDetail" : `/orders/${order.id}`;
};

const getProductRoute = (order: SalarOrder, item: OrderItem): string => {
    const isReferral =
        order.purchaseType === "MLM" || item.sourceType === "REFERRAL";
    return isReferral
        ? `/Refproductdetails/${item.productId}`
        : `/DetailPage/${item.productId}`;
};

const OrdersPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { cartCount } = useCart();
    const [browsingHistoryOpen, setBrowsingHistoryOpen,] = useState(false);
    const [activeTab, setActiveTab,] = useState<OrdersTab>("orders");
    const [search, setSearch] = useState("");
    const [period, setPeriod] = useState("3-months");

    // Server-side filter state
    const [serverSearch, setServerSearch] = useState("");
    const [date, setDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedLimit, setSelectedLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Redux state
    const {
        orders: apiOrders,
        loading,
        error,
        totalPages,
        page: reduxPage,
        actionsMap,
    } = useSelector((state: RootState) => state.customerOrders);
    const { keepShopping, keepShoppingLoaded } = useSelector((state: RootState) => state.activity);
    const buyAgainCards = useSelector((state: RootState) => state.buyAgain.cards);
    const buyAgainLastFetchedAt = useSelector((state: RootState) => state.buyAgain.lastFetchedAt);

    // Transform API orders to UI orders
    const transformedOrders = useMemo(() => {
        return transformOrdersToSalarOrders(apiOrders);
    }, [apiOrders]);

    // Merge action eligibility (canCancel/canReturn/canReplace) from actionsMap
    const ordersWithActions = useMemo(() => {
        return transformedOrders.map((order) => {
            const actions = actionsMap[order.id];
            return {
                ...order,
                canCancel: actions?.canCancel,
                canReturn: actions?.canReturn,
                canReplace: actions?.canReplace,
            };
        });
    }, [transformedOrders, actionsMap]);

    const visibleOrders = useMemo(() => {
        const query = search.trim().toLowerCase();
        const source = activeTab === "not-shipped" ? ordersWithActions.filter((order) => order.status === "processing") : ordersWithActions;
        return source.filter(
            (order) => !query ||
                order.id.toLowerCase().includes(query) ||
                order.orderNo.toLowerCase().includes(query) ||
                order.items.some(
                    (item) =>
                        item.title.toLowerCase().includes(query) ||
                        item.description.toLowerCase().includes(query) ||
                        item.sellerName.toLowerCase().includes(query)
                ));
    },
        [activeTab, search, ordersWithActions]
    );

    // "Buy it again" sidebar items — use the enriched buyAgain Redux slice
    // (same data source as BuyAgainPage): live catalog prices, images, ratings,
    // deals, sorted by most-recent purchase. Falls back to deriving from the
    // locally-fetched orders while the buyAgain slice is loading.
    const dynamicBuyAgainItems = useMemo(() => {
        // 1. Prefer the enriched buyAgain cards (sorted by most recent purchase)
        if (buyAgainCards.length > 0) {
            return buyAgainCards.slice(0, 4).map((card: BuyAgainCard) => ({
                id: card.productId,
                productId: card.productId,
                sourceId: card.productId,
                title: card.title,
                image: card.live?.mainImage || card.orderImage,
                price: card.live?.price ?? card.orderPrice,
                orderNo: card.lastOrderNo,
                orderDate: new Date(card.lastOrderDate).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }),
            }));
        }

        // 2. Fallback: derive from locally-fetched orders, sorted by date desc
        const eligible = ordersWithActions
            .filter(
                (order) =>
                    order.status === "delivered" ||
                    order.paymentStatus === "Success" ||
                    order.paymentStatus === "COMPLETED",
            )
            .slice()
            .sort((a, b) => new Date(b.placedDate).getTime() - new Date(a.placedDate).getTime());
        const seen = new Set<string>();
        const items: Array<{
            id: string;
            productId: string;
            sourceId: string;
            title: string;
            image: string;
            price: number;
            orderNo: string;
            orderDate: string;
        }> = [];
        for (const order of eligible) {
            for (const item of order.items) {
                const key = item.productId || item.sourceId;
                if (!key || seen.has(key)) continue;
                seen.add(key);
                items.push({
                    id: key,
                    productId: item.productId,
                    sourceId: item.sourceId,
                    title: item.title,
                    image: item.image,
                    price: item.price,
                    orderNo: order.orderNo,
                    orderDate: order.placedDate,
                });
            }
            if (items.length >= 4) break;
        }
        return items;
    }, [buyAgainCards, ordersWithActions]);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [actionType, setActionType] = useState<ActionType | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<SalarOrder | null>(null);
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    // Chat Support State
    const [chatOpen, setChatOpen] = useState(false);
    const [selectedOrderForChat, setSelectedOrderForChat] = useState<SalarOrder | null>(null);

    // Payment retry state
    const [retryLoadingId, setRetryLoadingId] = useState<string | null>(null);

    // Track which orders we've already fetched actions for (avoid infinite loops)
    const fetchedActionsRef = useRef<Set<string>>(new Set());

    // Fetch orders on component mount and when server-side filters change
    const fetchOrders = (page: number = currentPage) => {
        dispatch(fetchMyOrders({
            page,
            limit: selectedLimit,
            search: serverSearch || undefined,
            startDate: date ? new Date(date).toISOString() : undefined,
            endDate: toDate ? new Date(toDate).toISOString() : undefined,
        }));
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchOrders(1);
        if (!keepShoppingLoaded) {
            dispatch(getKeepShopping(5));
        }
        // Trigger buyAgain slice fetch (5-min cache prevents duplicates if
        // BuyAgainPage was already visited). Enriches the sidebar with live
        // catalog prices/images sorted by most-recent purchase.
        const cacheMaxAge = 5 * 60 * 1000;
        const isStale = !buyAgainLastFetchedAt || Date.now() - buyAgainLastFetchedAt > cacheMaxAge;
        if (isStale) dispatch(fetchBuyAgainProducts());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedLimit, buyAgainLastFetchedAt]);

    // Fetch order actions for each order (guarded to avoid infinite loops)
    useEffect(() => {
        ordersWithActions.forEach((order) => {
            if (!fetchedActionsRef.current.has(order.id)) {
                fetchedActionsRef.current.add(order.id);
                dispatch(fetchOrderActions(order.id));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordersWithActions]);

    const handleServerSearch = () => {
        setCurrentPage(1);
        fetchOrders(1);
    };

    const onPageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchOrders(newPage);
    };

    const handleRetryPayment = async (paymentId: string) => {
        setRetryLoadingId(paymentId);
        try {
            const token = getCookie("access_token");
            const baseUrl = [
                process.env.REACT_APP_API_BASE_URL,
                process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX,
            ].filter(Boolean).join("");

            const response = await axios.post(
                `${baseUrl}/payments/${paymentId}/retry`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success && response.data?.data?.paymentSessionId) {
                await startCashfreeCheckout(
                    response.data.data.paymentSessionId,
                    response.data.data.paymentMode,
                );
            } else if (response.data?.success && response.data?.data?.redirectUrl) {
                window.location.href = response.data.data.redirectUrl;
            } else if (response.data?.success && response.data?.data?.status === 'COMPLETED') {
                alert("Payment completed successfully!");
                fetchOrders(currentPage);
            } else {
                alert("Failed to initiate payment retry");
            }
        } catch (err: any) {
            alert(err.response?.data?.message || "An error occurred while retrying payment");
        } finally {
            setRetryLoadingId(null);
        }
    };

    const openBuyAgainPage = (sourceId?: string | number) => {
        navigate("/buy-again",
            sourceId ? {
                state: { sourceProductId: String(sourceId), },
            } : undefined);
    };
    const openProduct = (order: SalarOrder, product: OrderItem | number) => {
        if (typeof product === "number") {
            navigate(`/DetailPage/${product}`);
            return;
        }
        navigate(getProductRoute(order, product));
    };

    const openActionModal = (order: SalarOrder, type: ActionType) => {
        setSelectedOrder(order);
        setActionType(type);
        setReason("");
        setDetails("");
        setShowModal(true);
    };

    const handleActionSubmit = async () => {
        if (!selectedOrder || !actionType) return;
        if (!reason.trim()) {
            alert("Please provide a reason");
            return;
        }

        setActionLoading(true);

        try {
            if (actionType === "CANCEL") {
                await dispatch(cancelOrder({ orderId: selectedOrder.id, reason })).unwrap();
            } else if (actionType === "RETURN") {
                await dispatch(requestReturnOrder({ orderId: selectedOrder.id, reason, details })).unwrap();
            } else if (actionType === "REPLACE") {
                await dispatch(requestReplacementOrder({ orderId: selectedOrder.id, reason, details })).unwrap();
            }
            setShowModal(false);
            fetchOrders(currentPage);
        } catch (err: any) {
            alert(err || `Failed to ${actionType.toLowerCase()} order`);
        } finally {
            setActionLoading(false);
        }
    };

    const openChat = (order: SalarOrder) => {
        setSelectedOrderForChat(order);
        setChatOpen(true);
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = PLACEHOLDER_IMAGE;
    };

    const isPaymentPending = (order: SalarOrder): boolean =>
        order.paymentStatus !== "Success" &&
        order.paymentStatus !== "Refunded" &&
        order.paymentStatus !== "COMPLETED" &&
        order.paymentStatus !== "REFUNDED";

    return (
        <>
            <div className="orders-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" />
            <div className="orders-secondary-navbar">
                <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)} />
            </div>
            <main className="orders-page">
                <div className="orders-shell">
                    <div className="orders-breadcrumb">
                        <button type="button" onClick={() => navigate("/HomePage")}>Your Account</button>
                        <ChevronRight size={15} />
                        <strong> Orders </strong>
                    </div>
                    <section className="orders-hero">
                        <div>
                            <span> PURCHASE CENTRE </span>
                            <h1> Your Orders </h1>
                            <p> Track deliveries, reopen purchased products, manage returns and access support from one place. </p>
                        </div>
                        <div className="orders-search-wrap">
                            <label className="orders-search">
                                <Search size={20} />
                                <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search product, seller or order ID" />
                            </label>
                            <button type="button" className="orders-search-button" onClick={handleServerSearch}> Search </button>
                        </div>
                    </section>
                    <section className="orders-control-bar">
                        <nav className="orders-tabs">
                            <button type="button" className={activeTab === "orders" ? "active" : ""}
                                onClick={() => setActiveTab("orders")}>
                                Orders
                            </button>
                            <button type="button" onClick={() => openBuyAgainPage()} >
                                Buy Again
                            </button>
                            <button type="button" className={activeTab === "not-shipped" ? "active" : ""} onClick={() => setActiveTab("not-shipped")}>
                                Not Yet Shipped
                            </button>
                        </nav>
                        <div className="orders-period-row">
                            <strong> {visibleOrders.length} orders </strong>
                            <label className="orders-filter-date">
                                <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
                            </label>
                            <label className="orders-filter-date">
                                <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
                            </label>
                            <label className="orders-filter-limit">
                                <select value={selectedLimit} onChange={(event) => setSelectedLimit(Number(event.target.value))}>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </label>
                            <label>
                                <select value={period}
                                    onChange={(event) => setPeriod(event.target.value)}>
                                    <option value="3-months"> Past 3 months </option>
                                    <option value="6-months"> Past 6 months </option>
                                    <option value="2026"> 2026 </option>
                                    <option value="2025"> 2025 </option>
                                </select>
                                <ChevronDown size={16} />
                            </label>
                        </div>
                    </section>
                    <div className="orders-layout">
                        <section className="orders-main-column">
                            {loading ? (
                                <div className="orders-loading">
                                    <div className="orders-spinner"></div>
                                    <p>Loading your orders...</p>
                                </div>
                            ) : error ? (
                                <div className="orders-error">
                                    <p>{error}</p>
                                    <button type="button" onClick={() => fetchOrders(currentPage)}>
                                        Retry
                                    </button>
                                </div>
                            ) : visibleOrders.length === 0 ? (
                                <div className="orders-empty">
                                    <p>No orders found</p>
                                </div>
                            ) : (
                                visibleOrders.map(
                                    (order) => (
                                    <article className="order-card" key={order.id}>
                                        <header className="order-card-header">
                                            <div className="order-header-left">
                                                <div className="order-header-status-pill">
                                                    {getStatusIcon(order.status)}
                                                    <span> {order.statusTitle} </span>
                                                </div>
                                                <div className="order-header-meta">
                                                    <div>
                                                        <span> ORDER PLACED </span>
                                                        <strong> {order.placedDate} </strong>
                                                    </div>
                                                    <div>
                                                        <span> TOTAL </span>
                                                        <strong> ₹ {formatPrice(order.total)} </strong>
                                                    </div>
                                                    <div>
                                                        <span> SHIP TO </span>
                                                        <button type="button">
                                                            {order.shipTo.name}
                                                            <ChevronDown size={15} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="order-header-id">
                                                <span> ORDER ID </span>
                                                <strong> {order.orderNo} </strong>
                                                <div className="order-header-links">
                                                    <button type="button" className="order-view-details-btn" onClick={() => navigate(getOrderDetailsRoute(order))}>
                                                        <FileText size={16} />
                                                        View Order Details
                                                    </button>
                                                    {order.invoiceAvailable && (
                                                        <button type="button" className="order-invoice-btn" onClick={() =>
                                                            navigate(`/Invoice/${order.id}`)}>
                                                            Invoice
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </header>
                                        <div className="order-status-message">
                                            <div>
                                                <CircleDot size={15} />
                                                <p> {order.statusMessage} </p>
                                            </div>
                                            <div className="order-status-message-right">
                                                {order.salarCoinsEarned > 0 && (
                                                    <span className="order-coins-earned">
                                                        <Star size={13} fill="currentColor" /> {order.salarCoinsEarned} coins earned
                                                    </span>
                                                )}
                                                {order.expectedDate && (
                                                    <strong> Expected{" "} {order.expectedDate} </strong>
                                                )}
                                            </div>
                                        </div>
                                        <div className="order-card-content">
                                            <div className="order-content-main">
                                                <div className="order-products">
                                                    {order.items.map(
                                                        (item) => (
                                                            <div className="order-product" key={item.id}>
                                                                <button type="button" className="order-product-image" onClick={() => openProduct(order, item)}>
                                                                    <img src={item.image} alt={item.title} onError={handleImageError} />
                                                                </button>
                                                                <div className="order-product-info">
                                                                    <button type="button" className="order-product-description-link" onClick={() => openProduct(order, item)}>
                                                                        <strong> {item.title} </strong>
                                                                        {item.description && (
                                                                            <span> {item.description} </span>
                                                                        )}
                                                                    </button>
                                                                    {item.variant && (
                                                                        <div className="order-product-meta">
                                                                            <span> {item.variant} </span>
                                                                            <span> Qty {item.quantity} </span>
                                                                        </div>
                                                                    )}
                                                                    {!item.variant && (
                                                                        <div className="order-product-meta">
                                                                            <span> Qty {item.quantity} </span>
                                                                        </div>
                                                                    )}
                                                                    <button type="button" className="order-product-seller" onClick={() => navigate(`/seller/${item.sellerSlug}`)}>
                                                                        Sold by {item.sellerName}
                                                                    </button>
                                                                    <div className="order-product-bottom">
                                                                        <strong> ₹ {formatPrice(item.price)} </strong>
                                                                        {item.mrp && (
                                                                            <del> ₹ {formatPrice(item.mrp)} </del>)}
                                                                    </div>
                                                                    <div className="order-product-inline">
                                                                        {isPaymentPending(order) ? (
                                                                            <button
                                                                                type="button"
                                                                                className="complete-payment"
                                                                                onClick={() => handleRetryPayment(order.id)}
                                                                                disabled={retryLoadingId === order.id}
                                                                            >
                                                                                {retryLoadingId === order.id ? "Processing..." : (<>
                                                                                    <Star size={17} /> Complete Payment
                                                                                </>)}
                                                                            </button>
                                                                        ) : (
                                                                            <button type="button" className="buy-again" onClick={() => openBuyAgainPage(item.sourceId)}>
                                                                                <RotateCcw size={17} /> Buy it again </button>
                                                                        )}
                                                                        <button type="button" className="view-product" onClick={() => openProduct(order, item)}>
                                                                            View Product
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>

                                            <aside className="order-action-column">
                                                <div className="order-payment-badge-row">
                                                    <span className={`order-payment-badge ${paymentStatusClass(order.paymentStatus)}`}>
                                                        {order.paymentStatus || "Pending"}
                                                    </span>
                                                </div>
                                                {(order.status === "out-for-delivery" || order.status === "shipped" || order.status ===
                                                    "delivered") && (
                                                        <button type="button" className="order-action-highlight"
                                                            onClick={() => navigate(`/TrackPackage/${order.id}`)}>
                                                            <Truck size={18} />
                                                            Track Package
                                                        </button>
                                                    )}
                                                {order.trackingUrl && (
                                                    <a
                                                        className="order-action-courier-link"
                                                        href={order.trackingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <Truck size={18} />
                                                        Track via Courier
                                                    </a>
                                                )}
                                                {(order.status === "out-for-delivery" || order.status === "shipped" || order.status === "processing") && (
                                                    <button type="button" onClick={() => navigate(getOrderDetailsRoute(order))}>
                                                        <FileText size={18} /> View or Edit Order
                                                    </button>
                                                )}
                                                {order.canCancel && (order.status === "out-for-delivery" || order.status === "shipped" || order.status === "processing") && (
                                                    <button type="button" onClick={() => openActionModal(order, "CANCEL")}>
                                                        <XCircle size={18} />
                                                        Request Cancellation
                                                    </button>
                                                )}
                                                {(order.canReturn || order.canReplace) && (
                                                    <button type="button" onClick={() => openActionModal(order, "RETURN")}>
                                                        <RotateCcw size={18} />
                                                        Return or Replacement
                                                    </button>
                                                )}
                                                {order.reviewEligible && (
                                                    <button type="button" onClick={() => navigate(`/SubmitReview/${order.items[0].productId}`)}>
                                                        <Star size={18} />
                                                        Write Product Review
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => openChat(order)}>
                                                    <Headphones size={18} />
                                                    Chat With Us
                                                </button>
                                            </aside>
                                        </div>
                                        {order.refundDetails.refundAmount > 0 && (
                                            <div className="order-refund-footer">
                                                <div>
                                                    <span> Refund Amount </span>
                                                    <strong> ₹ {formatPrice(order.refundDetails.refundAmount)} </strong>
                                                </div>
                                                <div>
                                                    <span> Refund Status </span>
                                                    <span className={`order-refund-badge ${refundStatusClass(order.refundDetails.refundStatus)}`}>
                                                        {order.refundDetails.refundStatus}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span> Delivery Status </span>
                                                    <span className="order-refund-badge refund-info">
                                                        {order.statusTitle}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </article>
                                )
                                )
                            )}
                            {!loading && visibleOrders.length > 0 && totalPages > 1 && (
                                <div className="orders-pagination">
                                    <Pagenation
                                        currentPage={reduxPage || currentPage}
                                        totalPages={totalPages}
                                        onPageChange={onPageChange}
                                    />
                                </div>
                            )}
                        </section>
                        <aside className="orders-side-column">
                            <section className="orders-side-card orders-buy-again-card">
                                <div className="orders-side-card-head">
                                    <div>
                                        <span> REORDER HUB </span>
                                        <h2> Buy it again </h2>
                                        <p> Products you may want to reorder. </p>
                                    </div>
                                    <button type="button" onClick={() => openBuyAgainPage()}> View All </button>
                                </div>
                                <div className="orders-buy-again-list">
                                    {dynamicBuyAgainItems.length === 0 ? (
                                        <p className="orders-buy-again-empty">No previous purchases to reorder yet.</p>
                                    ) : (
                                        dynamicBuyAgainItems.map((item) => (
                                            <article key={item.id}>
                                                <button type="button" className="orders-buy-again-image" onClick={() => openProduct({ purchaseType: "ONLINE" } as SalarOrder, { productId: item.productId, sourceId: item.sourceId } as OrderItem)}>
                                                    <img src={item.image} alt={item.title} onError={handleImageError} />
                                                </button>
                                                <div>
                                                    <button type="button" className="orders-buy-again-title" onClick={() => openProduct({ purchaseType: "ONLINE" } as SalarOrder, { productId: item.productId, sourceId: item.sourceId } as OrderItem)}>
                                                        {item.title}
                                                    </button>
                                                    <div className="orders-buy-again-meta">
                                                        <small> Purchased {item.orderDate} </small>
                                                    </div>
                                                    <div className="orders-buy-again-price">
                                                        <strong> ₹ {formatPrice(item.price)} </strong>
                                                    </div>
                                                    <button type="button" className="orders-buy-again-button" onClick={() => openBuyAgainPage(item.sourceId)}>
                                                        Buy Again
                                                    </button>
                                                </div>
                                            </article>
                                        ))
                                    )}
                                </div>
                            </section>

                            <section className="orders-side-card orders-recent-card">
                                <div className="orders-side-card-head">
                                    <div>
                                        <span> RECENT ACTIVITY </span>
                                        <h2> Recently Viewed </h2>
                                        <p> Continue browsing products you opened recently. </p>
                                    </div>
                                </div>
                                <div className="orders-recent-list">
                                    {keepShopping.length === 0 ? (
                                        <p className="orders-recent-empty">No recently viewed products yet.</p>
                                    ) : (
                                        keepShopping.map((item) => (
                                            <article
                                                key={item._id}
                                                onClick={() => openProduct({ purchaseType: "ONLINE" } as SalarOrder, { productId: item._id, sourceId: item._id } as OrderItem)}
                                            >
                                                <div>
                                                    <img src={item.mainImage} alt={item.name} onError={handleImageError} />
                                                </div>
                                                <div>
                                                    <strong> {item.name} </strong>
                                                    {item.reviews && (
                                                        <span>
                                                            <Star size={14} fill="currentColor" />
                                                            {item.reviews.averageRating}
                                                        </span>
                                                    )}
                                                    {item.interactedAt && (
                                                        <small>
                                                            Viewed {new Date(item.interactedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                                                        </small>
                                                    )}
                                                </div>
                                            </article>
                                        ))
                                    )}
                                </div>
                            </section>
                            <section className="orders-side-card orders-assurance-card">
                                <div>
                                    <ShieldCheck size={22} />
                                    <span> SALAR ASSIST </span>
                                </div>
                                <h3> Need help with an order? </h3>
                                <p> Open an order to view delivery updates, refunds, replacements and support options.</p>
                                <button type="button" onClick={() => navigate("/support")}>
                                    Open Support
                                </button>
                            </section>
                        </aside>
                    </div>
                </div>
            </main>

            {/* Action Modal Backdrop */}
            {showModal && (
                <div
                    className="orders-modal-backdrop"
                    onClick={() => !actionLoading && setShowModal(false)}
                />
            )}

            {/* Action Modal */}
            {showModal && (
                <div className="orders-modal">
                    <div className="orders-modal-dialog">
                        <div className="orders-modal-content">
                            <div className="orders-modal-header">
                                <h5>
                                    {actionType === "CANCEL"
                                        ? "Cancel Order"
                                        : actionType === "RETURN"
                                            ? "Request Return"
                                            : "Request Replacement"}
                                </h5>
                                <button
                                    type="button"
                                    className="orders-modal-close"
                                    onClick={() => setShowModal(false)}
                                    disabled={actionLoading}
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="orders-modal-body">
                                <p className="orders-modal-subtitle">
                                    {actionType === "CANCEL"
                                        ? "Are you sure you want to cancel this order? This action cannot be undone."
                                        : `Please provide the reason and any additional details for your ${actionType?.toLowerCase()} request.`}
                                </p>
                                <div className="orders-modal-field">
                                    <label>Reason</label>
                                    <select
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        disabled={actionLoading}
                                    >
                                        <option value="">Select a reason</option>
                                        {actionType === "CANCEL" ? (
                                            <>
                                                <option value="Changed my mind">Changed my mind</option>
                                                <option value="Found a better price">Found a better price</option>
                                                <option value="Delivery is too slow">Delivery is too slow</option>
                                                <option value="Ordered by mistake">Ordered by mistake</option>
                                                <option value="Other">Other</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="Defective item">Defective item</option>
                                                <option value="Wrong item received">Wrong item received</option>
                                                <option value="Quality not as expected">Quality not as expected</option>
                                                <option value="Damaged during transit">Damaged during transit</option>
                                                <option value="Other">Other</option>
                                            </>
                                        )}
                                    </select>
                                </div>

                                {(actionType === "RETURN" || actionType === "REPLACE") && (
                                    <div className="orders-modal-field">
                                        <label>Additional details (optional)</label>
                                        <textarea
                                            placeholder="Additional details (optional)"
                                            rows={3}
                                            value={details}
                                            onChange={(e) => setDetails(e.target.value)}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="orders-modal-footer">
                                <button
                                    type="button"
                                    className="orders-modal-cancel-btn"
                                    onClick={() => setShowModal(false)}
                                    disabled={actionLoading}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className={`orders-modal-submit-btn ${actionType === "CANCEL" ? "danger" : "primary"}`}
                                    onClick={handleActionSubmit}
                                    disabled={actionLoading || !reason}
                                >
                                    {actionLoading && (
                                        <span className="orders-modal-spinner" />
                                    )}
                                    {actionType === "CANCEL"
                                        ? "Confirm Cancel"
                                        : actionType === "RETURN"
                                            ? "Submit Return"
                                            : "Submit Replacement"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Support Chat Panel */}
            <ChatPanel
                open={chatOpen}
                onClose={() => {
                    setChatOpen(false);
                    setSelectedOrderForChat(null);
                }}
                order={selectedOrderForChat as any}
            />
        </>
    );
};

export default OrdersPage;
